/**
 * User-facing labels for backend enums.
 *
 * The enums in `@canvas-sync/shared` (RunStatus, UploadStatus, TriggerType,
 * CanvasEnv) are the wire format and never change. This module translates
 * them into plain-English strings for the UI so non-engineers don't have
 * to read SHOUTY_CASE pipeline jargon.
 *
 * Use `runStatusLabel` / `uploadStatusLabel` in badges + table cells; use
 * `runStatusPhrase` when a status is interpolated into a sentence ("this
 * sync is in progress").
 */
import type {
  CanvasEnv,
  RunStatus,
  RunSummary,
  TriggerType,
  UploadStatus,
  UploadTriggerType,
} from "@canvas-sync/shared";

const RUN_STATUS_LABELS: Record<RunStatus, string> = {
  QUEUED: "Waiting",
  RUNNING: "In progress",
  SUCCEEDED: "Done",
  FAILED: "Failed",
  CANCELLED: "Canceled",
};

const RUN_STATUS_PHRASES: Record<RunStatus, string> = {
  QUEUED: "waiting",
  RUNNING: "in progress",
  SUCCEEDED: "succeeded",
  FAILED: "failed",
  CANCELLED: "canceled",
};

export function runStatusLabel(s: RunStatus): string {
  return RUN_STATUS_LABELS[s] ?? s;
}

export function runStatusPhrase(s: RunStatus): string {
  return RUN_STATUS_PHRASES[s] ?? s.toLowerCase();
}

const UPLOAD_STATUS_LABELS: Record<UploadStatus, string> = {
  SUBMITTING: "Sending",
  SUBMITTED: "Waiting for Canvas",
  IMPORTING: "Processing",
  IMPORTED: "Imported",
  IMPORTED_WITH_MESSAGES: "Imported with warnings",
  FAILED_WITH_MESSAGES: "Partially failed",
  FAILED: "Failed",
  ABORTED: "Canceled in Canvas",
  ERROR: "Could not reach Canvas",
};

export function uploadStatusLabel(s: UploadStatus): string {
  return UPLOAD_STATUS_LABELS[s] ?? s;
}

const TRIGGER_LABELS: Record<TriggerType, string> = {
  manual: "Manual",
  n8n: "Scheduled",
  scheduled: "Scheduled",
  rerun: "Rerun",
};

export function triggerLabel(t: TriggerType | string): string {
  return TRIGGER_LABELS[t as TriggerType] ?? t;
}

const UPLOAD_TRIGGER_LABELS: Record<UploadTriggerType, string> = {
  manual: "Manual",
  auto: "Automatic",
};

export function uploadTriggerLabel(t: UploadTriggerType | string): string {
  return UPLOAD_TRIGGER_LABELS[t as UploadTriggerType] ?? t;
}

const CANVAS_ENV_LABELS: Record<CanvasEnv, string> = {
  prod: "Production",
  test: "Test",
};

export function canvasEnvLabel(e: CanvasEnv): string {
  return CANVAS_ENV_LABELS[e] ?? e;
}

/**
 * Per-row submission state for the Syncs list. Combines the run's
 * auto-submit intent (`submitToCanvas`) with the denormalized
 * `latestUploadStatus` written back by the upload pipeline so the column
 * can show "Submitted" / "Submit failed" / etc. without a join.
 *
 * The state set is deliberately wider than UploadStatus so partial-success
 * outcomes (IMPORTED_WITH_MESSAGES, FAILED_WITH_MESSAGES) don't get
 * flattened into clean Submitted/Failed buckets — flattening hides real
 * Canvas-side issues from the list view.
 */
export type SubmissionState =
  | "submitted" // IMPORTED — Canvas accepted every row cleanly
  | "submittedWithWarnings" // IMPORTED_WITH_MESSAGES — accepted, but flagged warnings
  | "submittedPartial" // FAILED_WITH_MESSAGES — some rows imported, some failed
  | "submitFailed" // FAILED / ABORTED / ERROR — total failure
  | "autoSubmitError" // auto-submit was on but the Lambda failed before creating an upload
  | "submitting" // upload in flight (Sending, Waiting for Canvas, Processing)
  | "shippedViaBundle" // confirmed: fix CSV shipped to Canvas inside a SIS bundle
  | "viaBundle" // scheduled run — posts via the SIS bundle, not a standalone upload (shipment not yet confirmed)
  | "pending" // run still running OR auto-submit window not yet expired
  | "nothingToSubmit" // run succeeded but found 0 ghosts — no fix CSV to send
  | "off" // a manual run opted out of auto-submit and nothing was submitted
  | "unknown"; // no info (typically old runs without the new fields)

const STALE_PENDING_MS = 60_000; // how long after run finish before "pending" → "off"

export function runSubmissionState(
  r: Pick<
    RunSummary,
    | "status"
    | "trigger"
    | "submitToCanvas"
    | "latestUploadId"
    | "latestUploadStatus"
    | "finishedAt"
    | "ghostsFound"
    | "sisBundleId"
    | "sisBundleFixIncluded"
  > & { autoSubmitError?: string },
  now: number = Date.now(),
): SubmissionState {
  // 0-ghost runs short-circuit before any other check: the worker found
  // nothing to fix, so there will never be an upload row regardless of the
  // submitToCanvas flag. Without this carve-out the badge would drift from
  // "Pending" to "Off" after 60s, which reads like the user opted out.
  if (r.status === "SUCCEEDED" && r.ghostsFound === 0 && !r.latestUploadId) {
    return "nothingToSubmit";
  }
  // If there's an upload, that's the source of truth — even if an earlier
  // auto-submit attempt errored, a later successful upload means everything
  // worked in the end.
  if (r.latestUploadId) {
    if (!r.latestUploadStatus) return "submitting";
    switch (r.latestUploadStatus) {
      case "IMPORTED":
        return "submitted";
      case "IMPORTED_WITH_MESSAGES":
        return "submittedWithWarnings";
      case "FAILED_WITH_MESSAGES":
        return "submittedPartial";
      case "SUBMITTING":
      case "SUBMITTED":
      case "IMPORTING":
        return "submitting";
      case "FAILED":
      case "ABORTED":
      case "ERROR":
        return "submitFailed";
      default:
        return "unknown";
    }
  }
  // No standalone upload. If a SIS bundle shipped this run's fix CSV, THAT
  // is the submission — scheduled runs post via the bundle, not a standalone
  // auto-submit. Surface it honestly instead of letting it fall through to
  // "Off" (which would read as "nothing was sent"). A bundle that shipped
  // SIS-only (fix held back for contradiction/drift) sets
  // sisBundleFixIncluded=false, so it correctly does NOT land here.
  if (r.sisBundleFixIncluded === true) return "shippedViaBundle";
  // No upload yet. If the AutoSubmit Lambda recorded an error, surface that
  // — otherwise the "stuck on starting" pattern looks indistinguishable
  // from a deliberate opt-out.
  if (r.autoSubmitError) return "autoSubmitError";
  if (r.submitToCanvas === false) {
    // n8n / scheduled runs ship via the SIS bundle pipeline — but ONLY when
    // a bundle actually links them via `setSisBundleOnRun`. That linkage is
    // the only positive evidence that "via SIS bundle" applies. Without it,
    // the run is just "Off" — even when `trigger === "n8n"`, because the S3
    // ObjectCreated trigger tags every dashboard-uploaded file as `n8n` too,
    // and those files (e.g., one-off test CSVs) never get bundled. Falling
    // through to `off` for the unlinked case is the honest read.
    //
    // Trade-off: a real n8n cron run shows "Off" during the ~10-min window
    // between worker start and the bundler matching it, instead of the old
    // anticipatory "Via SIS bundle". Once `sisBundleId` is set the label
    // flips to "Via SIS bundle"; once `sisBundleFixIncluded === true` it
    // becomes "Shipped via SIS bundle" (handled at the top of this fn).
    if ((r.trigger === "n8n" || r.trigger === "scheduled") && r.sisBundleId) {
      return "viaBundle";
    }
    return "off";
  }
  if (r.submitToCanvas === undefined) return "unknown";
  // submitToCanvas === true with no upload yet.
  if (r.status !== "SUCCEEDED") return "pending";
  // Run finished but no upload row yet. Auto-submit Lambda usually runs in
  // <15s; we give it 60s before assuming it gave up.
  const finishedMs = r.finishedAt ? new Date(r.finishedAt).getTime() : now;
  return now - finishedMs < STALE_PENDING_MS ? "pending" : "off";
}

const SUBMISSION_LABELS: Record<SubmissionState, string> = {
  submitted: "Submitted",
  submittedWithWarnings: "Submitted (warnings)",
  submittedPartial: "Partially failed",
  submitFailed: "Submit failed",
  autoSubmitError: "Auto-submit error",
  submitting: "Submitting…",
  shippedViaBundle: "Shipped via SIS bundle",
  viaBundle: "Via SIS bundle",
  pending: "Pending",
  nothingToSubmit: "Nothing to submit",
  off: "Off",
  unknown: "—",
};

export function submissionLabel(s: SubmissionState): string {
  return SUBMISSION_LABELS[s];
}

/**
 * Submission states that are still in motion. List views use this to keep
 * polling on the fast cadence while a fix CSV is mid-flight to Canvas, even
 * after the run itself reached a terminal status. Without it the Syncs
 * dashboard drops to the slow idle poll the moment the run succeeds, so a
 * freshly-submitted upload's badge would visibly lag behind Canvas.
 */
const IN_FLIGHT_SUBMISSION_STATES: ReadonlySet<SubmissionState> = new Set([
  "submitting",
  "pending",
]);

export function isSubmissionInFlight(s: SubmissionState): boolean {
  return IN_FLIGHT_SUBMISSION_STATES.has(s);
}
